import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRightIcon, BookmarkIcon, CheckIcon, ZapIcon } from 'lucide-react';
import Button from '../../components/Button';
const WhatIsGPTStore = () => {
  return <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-394">
      <header className="py-12" data-id="element-395">
        <div className="text-sm font-semibold text-indigo-600 uppercase tracking-wider mb-2" data-id="element-396">
          Executive Guide • GPT Store
        </div>
        <h1 className="text-4xl sm:text-5xl font-bold leading-tight mb-6" data-id="element-397">
          What is the GPT Store? A Complete Guide for Brands in 2025
        </h1>
        <p className="text-xl text-gray-600 mb-8" data-id="element-398">
          The GPT Store is OpenAI's marketplace for custom GPTs—AI agents your
          customers can discover, install, and use. For brands, it's a new
          distribution channel for guided sales, support, and revenue.
        </p>
        <div className="flex flex-wrap gap-4" data-id="element-399">
          <a href="mailto:ben@shopilots.com?subject=Launch%20our%20GPT%20Store%20with%20Shopilots" className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700" data-id="element-400">
            Book a Strategy Call
          </a>
          <a href="tel:+19146011670" className="inline-flex items-center px-6 py-3 border border-gray-300 text-base font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50" data-id="element-401">
            US: (914) 601-1670
          </a>
          <a href="tel:+972505450460" className="inline-flex items-center px-6 py-3 border border-gray-300 text-base font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50" data-id="element-402">
            International: +972-50-545-0460
          </a>
        </div>
      </header>
      <nav className="bg-gray-50 p-6 rounded-xl mb-12" aria-label="Table of contents" data-id="element-403">
        <strong className="block mb-4 text-lg font-medium" data-id="element-404">On this page</strong>
        <ol className="list-decimal pl-5 space-y-2" data-id="element-405">
          <li data-id="element-406">
            <a href="#what-is" className="text-indigo-600 hover:text-indigo-800" data-id="element-407">
              What is the GPT Store?
            </a>
          </li>
          <li data-id="element-408">
            <a href="#why-brands" className="text-indigo-600 hover:text-indigo-800" data-id="element-409">
              Why it matters for brands
            </a>
          </li>
          <li data-id="element-410">
            <a href="#how-it-works" className="text-indigo-600 hover:text-indigo-800" data-id="element-411">
              How access, publishing & monetization work
            </a>
          </li>
          <li data-id="element-412">
            <a href="#requirements" className="text-indigo-600 hover:text-indigo-800" data-id="element-413">
              Publishing requirements & review
            </a>
          </li>
          <li data-id="element-414">
            <a href="#pitfalls" className="text-indigo-600 hover:text-indigo-800" data-id="element-415">
              Common pitfalls (and how Shopilots prevents them)
            </a>
          </li>
          <li data-id="element-416">
            <a href="#flow" className="text-indigo-600 hover:text-indigo-800" data-id="element-417">
              Visual: from build to monetization
            </a>
          </li>
          <li data-id="element-418">
            <a href="#faq" className="text-indigo-600 hover:text-indigo-800" data-id="element-419">
              FAQs: Data, privacy & store access
            </a>
          </li>
          <li data-id="element-420">
            <a href="#cta" className="text-indigo-600 hover:text-indigo-800" data-id="element-421">
              Get help: launch with Shopilots
            </a>
          </li>
        </ol>
      </nav>
      <section id="what-is" className="py-8 border-t border-gray-200" data-id="element-422">
        <h2 className="text-3xl font-bold mb-6" data-id="element-423">What is the GPT Store?</h2>
        <p className="mb-4 text-gray-700" data-id="element-424">
          The GPT Store is a marketplace inside ChatGPT where people discover
          and use specialized <em data-id="element-425">custom GPTs</em> (like apps—but
          conversational). Builders and brands can publish GPTs for public
          discovery, and teams can distribute internal-only GPTs across the
          organization.
        </p>
        <p className="text-gray-700" data-id="element-426">
          Learn more from OpenAI's official resources:{' '}
          <a href="https://help.openai.com/en/articles/8798868-introducing-the-gpt-store" target="_blank" rel="noopener noreferrer" className="text-indigo-600 hover:underline" data-id="element-427">
            Help Center overview
          </a>
          ,{' '}
          <a href="https://openai.com/index/introducing-the-gpt-store/" target="_blank" rel="noopener noreferrer" className="text-indigo-600 hover:underline" data-id="element-428">
            launch blog
          </a>
          , and the{' '}
          <a href="https://help.openai.com/en/articles/8554407-gpts-faq" target="_blank" rel="noopener noreferrer" className="text-indigo-600 hover:underline" data-id="element-429">
            GPTs FAQ
          </a>
          .
        </p>
      </section>
      <section id="why-brands" className="py-8 border-t border-gray-200" data-id="element-430">
        <h2 className="text-3xl font-bold mb-6" data-id="element-431">
          Why the GPT Store matters for brands
        </h2>
        <div className="grid md:grid-cols-2 gap-6" data-id="element-432">
          <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100" data-id="element-433">
            <h3 className="text-xl font-semibold mb-3" data-id="element-434">Always-on engagement</h3>
            <p className="text-gray-700" data-id="element-435">
              Be present where customers already chat. Your GPT can qualify
              needs, recommend products, and answer objections 24/7.
            </p>
          </div>
          <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100" data-id="element-436">
            <h3 className="text-xl font-semibold mb-3" data-id="element-437">Need-led shopping</h3>
            <p className="text-gray-700" data-id="element-438">
              Move beyond static product pages. Let customers describe their use
              case; the GPT returns the right SKUs with persuasive reasons to
              buy.
            </p>
          </div>
          <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100" data-id="element-439">
            <h3 className="text-xl font-semibold mb-3" data-id="element-440">Monetization paths</h3>
            <p className="text-gray-700" data-id="element-441">
              Combine OpenAI's programs with in-chat commerce, affiliate flows,
              and lead capture—turning conversation into revenue.
            </p>
          </div>
          <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100" data-id="element-442">
            <h3 className="text-xl font-semibold mb-3" data-id="element-443">
              First-mover advantage
            </h3>
            <p className="text-gray-700" data-id="element-444">
              As with early mobile apps, brands that establish category
              leadership in the store get compounding discovery benefits.
            </p>
          </div>
        </div>
      </section>
      <section id="how-it-works" className="py-8 border-t border-gray-200" data-id="element-445">
        <h2 className="text-3xl font-bold mb-6" data-id="element-446">
          How access, publishing & monetization work
        </h2>
        <div className="grid md:grid-cols-3 gap-6 mb-6" data-id="element-447">
          <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100" data-id="element-448">
            <span className="inline-flex items-center px-3 py-0.5 rounded-full text-sm font-medium bg-indigo-100 text-indigo-800 mb-4" data-id="element-449">
              Access
            </span>
            <p className="text-gray-700 mb-3" data-id="element-450">
              Users browse and install GPTs inside ChatGPT under{' '}
              <em data-id="element-451">Explore GPTs</em>. Teams can distribute internal GPTs via
              Enterprise/Team policies.
            </p>
            <p className="text-sm text-gray-500" data-id="element-452">
              <a href="https://help.openai.com/en/articles/8555535-gpts-chatgpt-enterprise-version" target="_blank" rel="noopener noreferrer" className="text-indigo-600 hover:underline" data-id="element-453">
                Enterprise GPTs
              </a>
            </p>
          </div>
          <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100" data-id="element-454">
            <span className="inline-flex items-center px-3 py-0.5 rounded-full text-sm font-medium bg-indigo-100 text-indigo-800 mb-4" data-id="element-455">
              Publish
            </span>
            <p className="text-gray-700 mb-3" data-id="element-456">
              Build your GPT (
              <a href="https://help.openai.com/en/articles/8554397-creating-a-gpt" target="_blank" rel="noopener noreferrer" className="text-indigo-600 hover:underline" data-id="element-457">
                Creating a GPT
              </a>
              ), add instructions, knowledge, and tools (web, images, actions),
              then submit to the store.
            </p>
            <p className="text-sm text-gray-500" data-id="element-458">
              <a href="https://help.openai.com/en/articles/8798878-building-and-publishing-a-gpt" target="_blank" rel="noopener noreferrer" className="text-indigo-600 hover:underline" data-id="element-459">
                Building & publishing
              </a>
            </p>
          </div>
          <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100" data-id="element-460">
            <span className="inline-flex items-center px-3 py-0.5 rounded-full text-sm font-medium bg-indigo-100 text-indigo-800 mb-4" data-id="element-461">
              Monetize
            </span>
            <p className="text-gray-700 mb-3" data-id="element-462">
              Design revenue around your funnel: direct conversions, lead
              capture, affiliates, or paid access—plus OpenAI's programs as
              available.
            </p>
            <p className="text-sm text-gray-500" data-id="element-463">
              <a href="https://openai.com/index/introducing-the-gpt-store/" target="_blank" rel="noopener noreferrer" className="text-indigo-600 hover:underline" data-id="element-464">
                Official announcement
              </a>
            </p>
          </div>
        </div>
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-6" data-id="element-465">
          <p className="font-semibold mb-1" data-id="element-466">Pro tip:</p>
          <p className="text-gray-700" data-id="element-467">
            Treat your GPT like a product. Define ICPs, jobs-to-be-done, and
            conversion events (demo booked, add-to-cart, email capture), then
            measure and iterate.
          </p>
        </div>
      </section>
      <section id="requirements" className="py-8 border-t border-gray-200" data-id="element-468">
        <h2 className="text-3xl font-bold mb-6" data-id="element-469">
          Publishing requirements & review
        </h2>
        <ul className="space-y-4 text-gray-700 list-disc pl-5" data-id="element-470">
          <li data-id="element-471">
            <strong data-id="element-472">Eligibility:</strong> Publishing requires a ChatGPT{' '}
            <em data-id="element-473">Plus/Pro</em> subscription or an <em data-id="element-474">Enterprise</em> workspace
            with GPT creation enabled.{' '}
            <a href="https://help.openai.com/en/articles/8798878-building-and-publishing-a-gpt" target="_blank" rel="noopener noreferrer" className="text-indigo-600 hover:underline" data-id="element-475">
              See details
            </a>
            .
          </li>
          <li data-id="element-476">
            <strong data-id="element-477">Builder profile verification:</strong> Verify your builder
            profile before public listing.{' '}
            <a href="https://help.openai.com/en/articles/8798878-building-and-publishing-a-gpt" target="_blank" rel="noopener noreferrer" className="text-indigo-600 hover:underline" data-id="element-478">
              How to verify
            </a>
            .
          </li>
          <li data-id="element-479">
            <strong data-id="element-480">Review process:</strong> Click <em data-id="element-481">Publish</em>; OpenAI
            reviews GPTs before they appear in the store.{' '}
            <a href="https://help.openai.com/en/?q=gpt%20store" target="_blank" rel="noopener noreferrer" className="text-indigo-600 hover:underline" data-id="element-482">
              Help Center
            </a>
            .
          </li>
          <li data-id="element-483">
            <strong data-id="element-484">Enterprise distribution:</strong> Admins can control who can
            create, share, and use GPTs.{' '}
            <a href="https://help.openai.com/en/articles/8555535-gpts-chatgpt-enterprise-version" target="_blank" rel="noopener noreferrer" className="text-indigo-600 hover:underline" data-id="element-485">
              Enterprise controls
            </a>
            .
          </li>
        </ul>
      </section>
      <section id="pitfalls" className="py-8 border-t border-gray-200" data-id="element-486">
        <h2 className="text-3xl font-bold mb-6" data-id="element-487">
          Common pitfalls (and how Shopilots prevents them)
        </h2>
        <div className="grid md:grid-cols-2 gap-6" data-id="element-488">
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-6" data-id="element-489">
            <h3 className="text-xl font-semibold mb-3" data-id="element-490">
              "Draft missing attributes" & setup snags
            </h3>
            <p className="text-gray-700 mb-3" data-id="element-491">
              Publishing errors are common—mismatched metadata, missing icons,
              or misconfigured actions. We maintain a pre-publish checklist and
              fix issues before submission.
            </p>
            <p className="text-sm text-gray-500" data-id="element-492">
              <a href="https://help.openai.com/en/articles/8554397-creating-a-gpt" target="_blank" rel="noopener noreferrer" className="text-indigo-600 hover:underline" data-id="element-493">
                Creating a GPT
              </a>
            </p>
          </div>
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-6" data-id="element-494">
            <h3 className="text-xl font-semibold mb-3" data-id="element-495">
              Low conversion & weak brand voice
            </h3>
            <p className="text-gray-700" data-id="element-496">
              Generic instructions → generic results. We encode your brand tone,
              objection handling, and offer logic so the GPT sells like your
              best rep.
            </p>
          </div>
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-6" data-id="element-497">
            <h3 className="text-xl font-semibold mb-3" data-id="element-498">
              Compliance & data concerns
            </h3>
            <p className="text-gray-700 mb-3" data-id="element-499">
              We align builds with OpenAI data controls and your org policies
              (history opt-outs, retention, residency where applicable). See:
            </p>
            <p className="text-gray-700" data-id="element-500">
              <a href="https://help.openai.com/en/articles/7730893-data-controls-faq" target="_blank" rel="noopener noreferrer" className="text-indigo-600 hover:underline" data-id="element-501">
                Data Controls
              </a>
              ,{' '}
              <a href="https://help.openai.com/en/articles/8983778-chat-and-file-retention-policies-in-chatgpt" target="_blank" rel="noopener noreferrer" className="text-indigo-600 hover:underline" data-id="element-502">
                Retention
              </a>
              ,{' '}
              <a href="https://help.openai.com/en/articles/9903489-data-residency-for-chatgpt" target="_blank" rel="noopener noreferrer" className="text-indigo-600 hover:underline" data-id="element-503">
                Data Residency
              </a>
              ,{' '}
              <a href="https://openai.com/enterprise-privacy/" target="_blank" rel="noopener noreferrer" className="text-indigo-600 hover:underline" data-id="element-504">
                Enterprise Privacy
              </a>
              .
            </p>
          </div>
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-6" data-id="element-505">
            <h3 className="text-xl font-semibold mb-3" data-id="element-506">Zero discoverability</h3>
            <p className="text-gray-700 mb-3" data-id="element-507">
              We optimize naming, categories, iconography, and descriptions for
              store search and featuring opportunities.{' '}
              <a href="https://help.openai.com/en/articles/8793007-getting-your-gpt-featured" target="_blank" rel="noopener noreferrer" className="text-indigo-600 hover:underline" data-id="element-508">
                Getting featured
              </a>
              .
            </p>
          </div>
        </div>
      </section>
      <section id="flow" className="py-8 border-t border-gray-200" data-id="element-509">
        <h2 className="text-3xl font-bold mb-6" data-id="element-510">
          Visual: from build to monetization
        </h2>
        <div className="bg-white border border-gray-200 rounded-xl p-6 mb-4" data-id="element-511">
          <div className="w-full h-40 md:h-56 relative" data-id="element-512">
            <svg className="w-full h-full" viewBox="0 0 920 220" role="img" aria-label="GPT Store flow: Access → Build → Review → Publish → Monetize" data-id="element-513">
              <defs data-id="element-514">
                <marker id="arrow" markerWidth="12" markerHeight="12" refX="10" refY="3" orient="auto" data-id="element-515">
                  <path d="M0,0 L0,6 L9,3 z" fill="#111" data-id="element-516"></path>
                </marker>
              </defs>
              <rect fill="#fff" stroke="#111" strokeWidth="2" rx="12" ry="12" x="20" y="60" width="150" height="80" data-id="element-517"></rect>
              <text x="95" y="105" textAnchor="middle" fontSize="14px" data-id="element-518">
                Access
              </text>
              <text x="95" y="125" textAnchor="middle" fontSize="14px" data-id="element-519">
                Explore GPTs
              </text>
              <rect fill="#fff" stroke="#111" strokeWidth="2" rx="12" ry="12" x="210" y="40" width="170" height="120" data-id="element-520"></rect>
              <text x="295" y="95" textAnchor="middle" fontSize="14px" data-id="element-521">
                Build
              </text>
              <text x="295" y="115" textAnchor="middle" fontSize="14px" data-id="element-522">
                Instructions · Tools · KB
              </text>
              <rect fill="#fff" stroke="#111" strokeWidth="2" rx="12" ry="12" x="420" y="60" width="160" height="80" data-id="element-523"></rect>
              <text x="500" y="95" textAnchor="middle" fontSize="14px" data-id="element-524">
                Review
              </text>
              <text x="500" y="115" textAnchor="middle" fontSize="14px" data-id="element-525">
                Policy & QA
              </text>
              <rect fill="#fff" stroke="#111" strokeWidth="2" rx="12" ry="12" x="610" y="60" width="150" height="80" data-id="element-526"></rect>
              <text x="685" y="95" textAnchor="middle" fontSize="14px" data-id="element-527">
                Publish
              </text>
              <text x="685" y="115" textAnchor="middle" fontSize="14px" data-id="element-528">
                Store listing
              </text>
              <rect fill="#fff" stroke="#111" strokeWidth="2" rx="12" ry="12" x="790" y="40" width="110" height="120" data-id="element-529"></rect>
              <text x="845" y="95" textAnchor="middle" fontSize="14px" data-id="element-530">
                Monetize
              </text>
              <text x="845" y="115" textAnchor="middle" fontSize="14px" data-id="element-531">
                Leads · Sales
              </text>
              <line x1="170" y1="100" x2="210" y2="100" stroke="#111" strokeWidth="2" markerEnd="url(#arrow)" data-id="element-532"></line>
              <line x1="380" y1="100" x2="420" y2="100" stroke="#111" strokeWidth="2" markerEnd="url(#arrow)" data-id="element-533"></line>
              <line x1="580" y1="100" x2="610" y2="100" stroke="#111" strokeWidth="2" markerEnd="url(#arrow)" data-id="element-534"></line>
              <line x1="760" y1="100" x2="790" y2="100" stroke="#111" strokeWidth="2" markerEnd="url(#arrow)" data-id="element-535"></line>
            </svg>
          </div>
        </div>
        <p className="text-sm text-gray-600" data-id="element-536">
          Shopilots treats your GPT like a product: we design the journey,
          validate compliance, optimize the listing, and wire in revenue.
        </p>
      </section>
      <section id="faq" className="py-8 border-t border-gray-200" data-id="element-537">
        <h2 className="text-3xl font-bold mb-6" data-id="element-538">
          FAQs: Data, privacy & store access
        </h2>
        <dl className="space-y-6" data-id="element-539">
          <div data-id="element-540">
            <dt className="font-bold text-lg mb-2" data-id="element-541">
              Does ChatGPT store my data?
            </dt>
            <dd className="text-gray-700" data-id="element-542">
              OpenAI provides controls for chat history, retention, and
              enterprise data policies. See{' '}
              <a href="https://help.openai.com/en/articles/7730893-data-controls-faq" target="_blank" rel="noopener noreferrer" className="text-indigo-600 hover:underline" data-id="element-543">
                Data Controls
              </a>
              ,{' '}
              <a href="https://help.openai.com/en/articles/8983778-chat-and-file-retention-policies-in-chatgpt" target="_blank" rel="noopener noreferrer" className="text-indigo-600 hover:underline" data-id="element-544">
                Retention
              </a>
              , and{' '}
              <a href="https://help.openai.com/en/articles/7039943-data-usage-for-consumer-services-faq" target="_blank" rel="noopener noreferrer" className="text-indigo-600 hover:underline" data-id="element-545">
                Data Usage FAQ
              </a>
              . Enterprise customers can set data residency in supported
              regions:{' '}
              <a href="https://help.openai.com/en/articles/9903489-data-residency-for-chatgpt" target="_blank" rel="noopener noreferrer" className="text-indigo-600 hover:underline" data-id="element-546">
                Data Residency
              </a>
              .
            </dd>
          </div>
          <div data-id="element-547">
            <dt className="font-bold text-lg mb-2" data-id="element-548">
              Who can publish to the GPT Store?
            </dt>
            <dd className="text-gray-700" data-id="element-549">
              Publishers need ChatGPT Plus/Pro or an Enterprise workspace with
              GPT creation enabled, a verified builder profile, and a compliant
              listing.{' '}
              <a href="https://help.openai.com/en/articles/8798878-building-and-publishing-a-gpt" target="_blank" rel="noopener noreferrer" className="text-indigo-600 hover:underline" data-id="element-550">
                Publishing requirements
              </a>
              .
            </dd>
          </div>
          <div data-id="element-551">
            <dt className="font-bold text-lg mb-2" data-id="element-552">
              How do I create a custom GPT?
            </dt>
            <dd className="text-gray-700" data-id="element-553">
              Use the GPT editor to set instructions, tools (web, images,
              actions), knowledge, and conversation starters—then click{' '}
              <em data-id="element-554">Publish</em>.{' '}
              <a href="https://help.openai.com/en/articles/8554397-creating-a-gpt" target="_blank" rel="noopener noreferrer" className="text-indigo-600 hover:underline" data-id="element-555">
                Creating a GPT
              </a>
              .
            </dd>
          </div>
          <div data-id="element-556">
            <dt className="font-bold text-lg mb-2" data-id="element-557">How do brands monetize?</dt>
            <dd className="text-gray-700" data-id="element-558">
              Design the funnel: lead capture, in-chat commerce, affiliates, or
              paid access. Monitor store analytics and iterate. For official
              programs and policy updates, follow the{' '}
              <a href="https://openai.com/index/introducing-the-gpt-store/" target="_blank" rel="noopener noreferrer" className="text-indigo-600 hover:underline" data-id="element-559">
                OpenAI blog
              </a>{' '}
              and{' '}
              <a href="https://help.openai.com/en/?q=gpt%20store" target="_blank" rel="noopener noreferrer" className="text-indigo-600 hover:underline" data-id="element-560">
                Help Center
              </a>
              .
            </dd>
          </div>
        </dl>
        <p className="text-sm text-gray-500 mt-4" data-id="element-561">
          Note: Policies and programs can change—always confirm the latest terms
          in the official docs above.
        </p>
      </section>
      <section id="cta" className="py-8 my-12 bg-gradient-to-r from-indigo-700 to-purple-700 text-white rounded-xl" data-id="element-562">
        <div className="p-8" data-id="element-563">
          <h2 className="text-3xl font-bold mb-6" data-id="element-564">
            Launch & Monetize Your GPT with Shopilots
          </h2>
          <p className="text-xl mb-6" data-id="element-565">
            We turn your catalog and brand know-how into a revenue-ready GPT:
            compliant, on-brand, and optimized for conversion.
          </p>
          <ul className="space-y-4 mb-8" data-id="element-566">
            <li className="flex items-start" data-id="element-567">
              <CheckIcon className="h-6 w-6 text-indigo-300 mr-2 flex-shrink-0 mt-0.5" data-id="element-568" />
              <span data-id="element-569">
                <strong className="font-semibold" data-id="element-570">
                  Done-for-you build & publish:
                </strong>{' '}
                instructions, datasets, tools, icons, listing, QA.
              </span>
            </li>
            <li className="flex items-start" data-id="element-571">
              <CheckIcon className="h-6 w-6 text-indigo-300 mr-2 flex-shrink-0 mt-0.5" data-id="element-572" />
              <span data-id="element-573">
                <strong className="font-semibold" data-id="element-574">
                  Monetization by design:
                </strong>{' '}
                lead capture flows, affiliate links, add-to-cart actions, offer
                logic.
              </span>
            </li>
            <li className="flex items-start" data-id="element-575">
              <CheckIcon className="h-6 w-6 text-indigo-300 mr-2 flex-shrink-0 mt-0.5" data-id="element-576" />
              <span data-id="element-577">
                <strong className="font-semibold" data-id="element-578">Compliance first:</strong>{' '}
                data controls, retention guidance, and enterprise policies
                aligned with OpenAI.
              </span>
            </li>
            <li className="flex items-start" data-id="element-579">
              <CheckIcon className="h-6 w-6 text-indigo-300 mr-2 flex-shrink-0 mt-0.5" data-id="element-580" />
              <span data-id="element-581">
                <strong className="font-semibold" data-id="element-582">Iterate & grow:</strong> store
                SEO, analytics loops, feature-readiness.
              </span>
            </li>
          </ul>
          <div className="flex flex-wrap gap-4" data-id="element-583">
            <a href="mailto:ben@shopilots.com?subject=Launch%20our%20GPT%20Store%20with%20Shopilots" className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md shadow-sm text-indigo-700 bg-white hover:bg-indigo-50" data-id="element-584">
              Book a Strategy Call
            </a>
            <a href="tel:+19146011670" className="inline-flex items-center px-6 py-3 border border-white text-base font-medium rounded-md text-white hover:bg-indigo-600" data-id="element-585">
              US: (914) 601-1670
            </a>
            <a href="tel:+972505450460" className="inline-flex items-center px-6 py-3 border border-white text-base font-medium rounded-md text-white hover:bg-indigo-600" data-id="element-586">
              International: +972-50-545-0460
            </a>
            <a href="https://wa.me/972505450460" target="_blank" rel="noopener noreferrer" className="inline-flex items-center px-6 py-3 border border-white text-base font-medium rounded-md text-white hover:bg-indigo-600" data-id="element-587">
              WhatsApp
            </a>
          </div>
        </div>
      </section>
      <footer className="py-8 border-t border-gray-200 text-sm text-gray-500" data-id="element-588">
        <p data-id="element-589">© Shopilots — AI-powered guided selling & GPT Store experts.</p>
      </footer>
    </div>;
};
export default WhatIsGPTStore;